import { formatTranslation } from 'intl';
import type { TranslationKey } from 'intl/types/translationKey';
import { CommonTranslationKeys } from 'intl/keys';
import {
  ReactNode,
  useState,
  useCallback,
  useContext,
  useRef,
  createContext,
  PropsWithChildren,
} from 'react';
import { useIntl } from 'react-intl';
import {
  Dialog as MaterialDialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Divider,
  CircularProgress,
} from '@mui/material';
import { ButtonContained, ButtonText } from '../../../general';
import { styles } from './dialogStyles';
import useIsMobile from 'hooks/useIsMobile';
import { Color } from 'shared/utils/colors';

export interface AsyncDialogOptions {
  title?: ReactNode;
  description?: ReactNode;
  submitLabel?: TranslationKey;
  cancelLabel?: TranslationKey;
  isDangerousAction?: boolean;
  maxWidth?: number;
}

export type AsyncDialogFactory = <T>(
  options: AsyncDialogOptions,
  onConfirm: () => Promise<T>
) => Promise<T>;

const AsyncDialogContext = createContext<AsyncDialogFactory | null>(null);

export function useAsyncDialog(): AsyncDialogFactory {
  const context = useContext(AsyncDialogContext);
  if (!context) {
    throw new Error(
      'useAsyncDialog must be used within an AsyncDialogProvider'
    );
  }
  return context;
}

const dialogProps = (isMobile: boolean, maxWidth?: number): object => ({
  style: {
    zIndex: 40000,
  },
  PaperProps: {
    style: {
      minWidth: isMobile ? 'min(95%, 300px)' : 500,
      maxWidth: maxWidth || 800,
    },
  },
});

interface DialogState {
  options: AsyncDialogOptions;
  onConfirm: () => Promise<unknown>;
}

export function AsyncDialogProvider({
  children,
}: PropsWithChildren<object>): JSX.Element {
  const isMobile = useIsMobile();
  const intl = useIntl();

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dialogState, setDialogState] = useState<DialogState | null>(null);

  const promiseRef = useRef<{
    resolve: (value: unknown) => void;
    reject: (reason?: unknown) => void;
  } | null>(null);

  const createAsyncDialog = useCallback(
    <T,>(
      options: AsyncDialogOptions,
      onConfirm: () => Promise<T>
    ): Promise<T> => {
      return new Promise<T>((resolve, reject) => {
        promiseRef.current = {
          resolve: resolve as (value: unknown) => void,
          reject,
        };
        setDialogState({
          options,
          onConfirm: onConfirm as () => Promise<unknown>,
        });
        setIsOpen(true);
        setIsLoading(false);
      });
    },
    []
  );

  const handleClose = useCallback((): void => {
    if (isLoading) return;

    setIsOpen(false);
    promiseRef.current?.reject();
    promiseRef.current = null;
    setDialogState(null);
  }, [isLoading]);

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (!dialogState?.onConfirm) return;

    setIsLoading(true);

    try {
      const result = await dialogState.onConfirm();
      setIsOpen(false);
      promiseRef.current?.resolve(result);
    } catch (error) {
      setIsOpen(false);
      promiseRef.current?.reject(error);
    } finally {
      setIsLoading(false);
      promiseRef.current = null;
      setDialogState(null);
    }
  }, [dialogState]);

  const options = dialogState?.options;

  let submitLabel = '';
  let cancelLabel = '';
  if (options) {
    let submitKey: TranslationKey = CommonTranslationKeys.yes;
    if (options.submitLabel) {
      submitKey = options.submitLabel;
    }
    let cancelKey: TranslationKey = CommonTranslationKeys.cancel;
    if (options.cancelLabel) {
      cancelKey = options.cancelLabel;
    }
    submitLabel = formatTranslation(intl, submitKey);
    cancelLabel = formatTranslation(intl, cancelKey);
  }

  return (
    <AsyncDialogContext.Provider value={createAsyncDialog}>
      {children}
      {options && (
        <MaterialDialog
          open={isOpen}
          onClose={handleClose}
          {...dialogProps(isMobile, options.maxWidth)}
        >
          <div style={styles.dialogHeader}>
            {options.title && (
              <DialogTitle id='alert-dialog-title'>{options.title}</DialogTitle>
            )}
          </div>
          <Divider />
          <DialogContent
            style={{
              ...styles.dialogContent,
              ...(options.isDangerousAction && {
                backgroundColor: Color.redWithOpacity,
              }),
            }}
          >
            {options.description}
          </DialogContent>
          <DialogActions style={styles.dialogActions}>
            <ButtonText
              onClick={handleClose}
              disabled={isLoading}
              data-qa='dialog-discard-button'
            >
              {cancelLabel}
            </ButtonText>
            <ButtonContained
              onClick={handleSubmit}
              disabled={isLoading}
              data-qa='dialog-confirm-button'
              endIcon={
                isLoading ? (
                  <CircularProgress size={16} color='inherit' />
                ) : undefined
              }
              {...(options.isDangerousAction && {
                sx: { backgroundColor: Color.red, color: Color.white },
              })}
            >
              {submitLabel}
            </ButtonContained>
          </DialogActions>
        </MaterialDialog>
      )}
    </AsyncDialogContext.Provider>
  );
}
